import {
  Activity,
  ClipboardCheck,
  Database,
  Keyboard,
  LogOut,
  MailPlus,
  Menu,
  Library,
  ShieldCheck,
  UsersRound,
  WalletCards,
  X
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "./api";
import { Wordmark } from "./brand";
import type { ModuleId } from "./types";
import { initials } from "./ui";

export const modules = [
  {
    id: "stacks" as const,
    label: "Stacks",
    key: "s",
    permission: "stacks.read",
    icon: Library
  },
  {
    id: "review" as const,
    label: "Review",
    key: "r",
    permission: "stacks.read",
    icon: ClipboardCheck
  },
  {
    id: "accounts" as const,
    label: "Accounts",
    key: "a",
    permission: "accounts.read",
    icon: WalletCards
  },
  {
    id: "invitations" as const,
    label: "Invitations",
    key: "v",
    permission: "invitations.manage",
    icon: MailPlus
  },
  {
    id: "telemetry" as const,
    label: "Telemetry",
    key: "t",
    permission: "telemetry.read",
    icon: Activity
  },
  {
    id: "investors" as const,
    label: "Investors",
    key: "i",
    permission: "investors.read",
    icon: UsersRound
  },
  {
    id: "cache" as const,
    label: "Legacy cache",
    key: "c",
    permission: "cache.read",
    icon: Database
  },
  {
    id: "staff" as const,
    label: "Staff",
    key: "f",
    permission: "staff.manage",
    icon: ShieldCheck
  }
];

export function Shell({
  session,
  active,
  onChange,
  onLogout,
  demo,
  children
}: {
  session: Session;
  active: ModuleId;
  onChange: (module: ModuleId) => void;
  onLogout: () => void;
  demo: boolean;
  children: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const visible = modules.filter((module) => session.permissions.includes(module.permission));
  const pendingG = useRef(false);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) || target?.isContentEditable;

      if (event.key === "Escape") {
        setShortcutsOpen(false);
        setNavOpen(false);
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "?") {
        event.preventDefault();
        setShortcutsOpen((open) => !open);
        return;
      }
      if (event.key === "g") {
        pendingG.current = true;
        window.setTimeout(() => (pendingG.current = false), 1200);
        return;
      }
      if (pendingG.current) {
        const module = visible.find((item) => item.key === event.key.toLowerCase());
        pendingG.current = false;
        if (module) {
          event.preventDefault();
          onChange(module.id);
          setNavOpen(false);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onChange]);

  return (
    <div className="app">
      {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <aside className={`sidebar${navOpen ? " open" : ""}`}>
        <div className="brand">
          <span className="brand-name">
            <Wordmark />
            <span>Admin console</span>
          </span>
          <button
            type="button"
            className="rail-icon-btn push"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <nav className="rail-nav" aria-label="Modules">
          <p className="rail-nav-label">Operations</p>
          {visible.map((module) => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                type="button"
                className="rail-item"
                aria-current={active === module.id ? "page" : undefined}
                onClick={() => {
                  onChange(module.id);
                  setNavOpen(false);
                }}
              >
                <Icon aria-hidden="true" />
                <span>{module.label}</span>
                <kbd aria-hidden="true">g {module.key}</kbd>
              </button>
            );
          })}
        </nav>

        <div className="rail-foot">
          {demo && (
            <p className="demo-tag">
              <i aria-hidden="true" />
              Illustrative data. No production service is connected.
            </p>
          )}
          <button
            type="button"
            className="rail-item"
            onClick={() => setShortcutsOpen(true)}
            aria-haspopup="dialog"
          >
            <Keyboard aria-hidden="true" />
            <span>Keyboard shortcuts</span>
            <kbd aria-hidden="true">?</kbd>
          </button>
          <div className="staff">
            <span className="avatar" aria-hidden="true">
              {initials(session.user.name)}
            </span>
            <span className="staff-name">
              <b>{session.user.name}</b>
              <span>{session.user.email}</span>
            </span>
            <button
              type="button"
              className="rail-icon-btn push"
              onClick={onLogout}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <div className="work">
        <header className="mobile-bar">
          <button
            type="button"
            className="rail-icon-btn"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
          >
            <Menu aria-hidden="true" />
          </button>
          <Wordmark />
        </header>
        {children}
      </div>

      {shortcutsOpen && <ShortcutSheet onClose={() => setShortcutsOpen(false)} />}
    </div>
  );
}

function ShortcutSheet({ onClose }: { onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => closeRef.current?.focus(), []);
  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
    >
      <div className="shortcut-card" onClick={(event) => event.stopPropagation()}>
        <header>
          <h2>Keyboard shortcuts</h2>
          <button
            type="button"
            className="icon-btn"
            style={{ marginLeft: "auto" }}
            onClick={onClose}
            ref={closeRef}
            aria-label="Close"
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <dl>
          {[
            ["Focus search", ["/"]],
            ["Move through records", ["↑", "↓"]],
            ["Close record or clear search", ["Esc"]],
            ["Go to stacks", ["g", "s"]],
            ["Go to review", ["g", "r"]],
            ["Go to accounts", ["g", "a"]],
            ["Go to invitations", ["g", "v"]],
            ["Go to telemetry", ["g", "t"]],
            ["Go to investors", ["g", "i"]],
            ["Go to legacy cache", ["g", "c"]],
            ["Go to staff", ["g", "f"]],
            ["Review: answer, note, record", ["S", "D", "U", "N", "Enter"]],
            ["Review: previous / next record", ["K", "J"]],
            ["Review: CourtListener, previous record, skip", ["O", "Z", "X"]],
            ["This list", ["?"]]
          ].map(([label, keys]) => (
            <div key={label as string}>
              <dt>{label}</dt>
              <dd>
                {(keys as string[]).map((key) => (
                  <kbd className="key" key={key}>
                    {key}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
